import { mobileAccess } from "#skyward_global";
import { getClosestContentPanel, registerLoadDOMCallback } from "#skyward_window";
import { JQueryInput } from "./types";

if (!mobileAccess)
{
    registerLoadDOMCallback(init);
}

export function focusAfter(selector: JQueryInput, tabsFromElement?: string | number)
{
    if (mobileAccess)
    {
        return;
    }
    let element = $(selector);

    if (element.length === 0)
    {
        element = $(document.activeElement as HTMLElement);
    }

    const skywardContentPanel = getSkywardContentPanel(element);
    const allFocusables = getFocusables(skywardContentPanel);

    if (allFocusables.length === 0)
    {
        return;
    }

    if (isNaN(parseInt(tabsFromElement as string)) || (tabsFromElement as number) < 0)
    {
        tabsFromElement = 1;
    }

    const sortedFocusablesByTabIndex: { [key: number]: JQuery<HTMLElement>[] } = {};
    let elementsIndexInSortedFocusables: number = 0;
    let elementsTabIndex = 0;

    allFocusables.each(function ()
    {
        const thisFocusable = $(this);
        let thisTabIndex = thisFocusable.attr("tabindex");
        if (thisTabIndex === undefined)
        {
            thisTabIndex = "0";
        }

        const thisTabIndexNum = parseInt(thisTabIndex);
        if (thisTabIndexNum >= 0)
        {
            if (!(sortedFocusablesByTabIndex[thisTabIndexNum]))
            {
                sortedFocusablesByTabIndex[thisTabIndexNum] = [];
            }
            if (element[0] === thisFocusable[0])
            {
                elementsIndexInSortedFocusables = sortedFocusablesByTabIndex[thisTabIndexNum].length;
                elementsTabIndex = thisTabIndexNum;
            }
            sortedFocusablesByTabIndex[thisTabIndexNum].push(thisFocusable);
        }
    });

    let movesRemaining = tabsFromElement as number;
    let focusedElement: JQuery<HTMLElement> | undefined;

    const indexOfNextFocus = elementsIndexInSortedFocusables + (tabsFromElement as number);
    const elementsLeftInCollection = sortedFocusablesByTabIndex[elementsTabIndex].length;
    if (indexOfNextFocus < elementsLeftInCollection)
    {
        focusedElement = sortedFocusablesByTabIndex[elementsTabIndex][indexOfNextFocus];
        setFocus(focusedElement);
        return;
    }
    else
    {
        movesRemaining -= elementsLeftInCollection - elementsIndexInSortedFocusables;
    }
    const sortedFocusablesKeys = Object.keys(sortedFocusablesByTabIndex);
    const keyPosition = sortedFocusablesKeys.indexOf(elementsTabIndex + "");

    // go to the next collection of tab-indexed items and look for a match
    for (let n = keyPosition + 1; n < sortedFocusablesKeys.length; n++)
    {
        const elementsInNextCollection = sortedFocusablesByTabIndex[parseInt(sortedFocusablesKeys[n])].length;
        if (movesRemaining <= elementsInNextCollection)
        {
            focusedElement = sortedFocusablesByTabIndex[parseInt(sortedFocusablesKeys[n])][movesRemaining];
            setFocus(focusedElement);
            return;
        }
        else
        {
            movesRemaining -= elementsInNextCollection;
        }
    }
    const defaultFocusedElement = sortedFocusablesByTabIndex[parseInt(sortedFocusablesKeys[0])][0];
    setFocus(defaultFocusedElement);
}

export function focusBefore(selector: JQueryInput)
{
    if (mobileAccess)
    {
        return;
    }

    const element = $(selector).get(0)!;
    const skywardContentPanel = getSkywardContentPanel(element);
    const focusables = getFocusables(skywardContentPanel);

    if (focusables.length === 0)
    {
        return;
    }

    let elementReceivingFocus: JQuery<HTMLElement> | null = null;

    for (let x = 0; x < focusables.length; x++)
    {
        if (focusables[x] === element)
        {
            elementReceivingFocus = $(x === 0 ? focusables[focusables.length - 1] : focusables[x - 1]);
            setFocus(elementReceivingFocus);
            break;
        }
    }
    if (elementReceivingFocus === null)
    {
        elementReceivingFocus = $(focusables[0]);
        setFocus(elementReceivingFocus);
    }
}

function getFocusables(scopedContainer: JQueryInput)
{
    const container = $(scopedContainer);
    const focusables = container.find(":focusable");

    return focusables;
}

function setFocus(elementParam: JQueryInput)
{
    const element = $(elementParam);
    if (element.length === 0)
    {
        return;
    }
    // Give Element Focus
    element.trigger("focus");

    // Select All Text In Element
    // Number input not supported (https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange)
    if (element.is("input[type='text'], input[type='search'], input[type='url'], input[type='tel'], input[type='password'], textarea"))
    {
        const domElement = element[0] as HTMLInputElement | HTMLTextAreaElement;
        if (domElement.value.length > 0)
        {
            domElement.setSelectionRange(0, domElement.value.length);
        }
    }
}

function getSkywardContentPanel(selector: JQueryInput)
{
    const element = $(selector);
    return element.prop("nodeName") === "HTML" ? element.find(".skywardContentPanel").first() : getClosestContentPanel(element);
}

function init(selector: JQueryInput, notifyDOMLoadType: { skywardContentPanel?: boolean })
{
    if (notifyDOMLoadType.skywardContentPanel)
    {
        if (selector !== "#detailsPanel")
        {
            const skywardContentPanel = getSkywardContentPanel(selector);

            if (skywardContentPanel.length > 0)
            {
                let focusedElement = skywardContentPanel.find("[autofocus]").first();
                if (focusedElement.length === 0)
                {
                    let minValue = Number.POSITIVE_INFINITY;
                    let minItem: HTMLElement | undefined;
                    getFocusables(skywardContentPanel).each(function ()
                    {
                        const val = parseInt(this.getAttribute("tabindex")!, 10);
                        if (val > 0 && val < minValue)
                        {
                            minItem = this;
                            minValue = val;
                        }
                    });
                    if (minItem !== undefined)
                    {
                        focusedElement = $(minItem);
                    }
                    else
                    {
                        focusedElement = $();
                    }
                }

                if (focusedElement.length > 0)
                {
                    setFocus(focusedElement);
                    focusedElement.trigger("select");
                }
            }
        }
    }
}